import React, { useState } from 'react';
import { Star, Send, Loader2 } from 'lucide-react';
import { useExecutionScores } from '../hooks/useExecutionScores';

interface ExecutionScoreCardProps {
  executionId: string;
}

export const ExecutionScoreCard: React.FC<ExecutionScoreCardProps> = ({ executionId }) => {
  const { scores, isLoading, submitScore } = useExecutionScores(executionId);
  const [selectedScore, setSelectedScore] = useState<number>(0);
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const handleScoreSelect = (score: number) => {
    setSelectedScore(score);
    setShowNoteInput(true);
  };

  const handleEdit = () => {
    if (scores?.myScore) {
      setSelectedScore(scores.myScore.score);
      setNote(scores.myScore.note || '');
      setShowNoteInput(true);
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    setShowNoteInput(false);
    setSelectedScore(0);
    setNote('');
    setIsEditing(false);
  };

  const handleSubmit = async () => {
    if (selectedScore === 0) return;
    if (selectedScore < 10 && !note.trim()) return;

    setIsSubmitting(true);
    try {
      await submitScore(selectedScore, note || undefined);
      setNote('');
      setShowNoteInput(false);
      setSelectedScore(0);
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to submit score:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const otherScores = scores?.scores.filter(s => s.userId !== scores.myScore?.userId) ?? [];

  if (isLoading && !scores) {
    return (
      <div data-component="ExecutionScoreCard" className="flex items-center justify-center p-4">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div data-component="ExecutionScoreCard" className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
      {/* Aggregate */}
      <div className="flex items-center gap-3">
        <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
        <span className="text-sm text-gray-500">Avg:</span>
        <span className="text-2xl font-bold text-gray-900">
          {scores && scores.totalScores > 0 ? scores.averageScore.toFixed(1) : '—'}
        </span>
        <span className="text-sm text-gray-500">/10</span>
        {scores && scores.totalScores > 0 && (
          <span className="text-sm text-gray-500">
            ({scores.totalScores} {scores.totalScores === 1 ? 'rating' : 'ratings'})
          </span>
        )}
      </div>

      {/* My Score Section */}
      <div className="border-t border-gray-200 pt-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">My Rating</h4>

        {scores?.myScore && !isEditing ? (
          <div data-flow="my-score-display" className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-blue-900">{scores.myScore.score}/10</span>
                  <span className="text-xs text-blue-600">
                    {new Date(scores.myScore.timestamp).toLocaleDateString()}
                  </span>
                </div>
                {scores.myScore.note && (
                  <p className="text-sm text-blue-700 italic">"{scores.myScore.note}"</p>
                )}
              </div>
              <button
                onClick={handleEdit}
                className="px-3 py-1 text-xs font-medium text-blue-700 hover:text-blue-900 hover:bg-blue-100 rounded transition-colors"
              >
                Edit
              </button>
            </div>
          </div>
        ) : (
          <div data-flow="my-score-input" className="space-y-3">
            {isEditing && (
              <div className="text-sm text-gray-600 mb-2">Update your rating:</div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              {!isEditing && <span className="text-sm text-gray-600">Rate this result:</span>}
              <div className="flex gap-1 flex-wrap">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
                  <button
                    key={score}
                    onClick={() => handleScoreSelect(score)}
                    disabled={isSubmitting}
                    className={`w-8 h-8 rounded text-sm font-medium transition-all ${
                      selectedScore === score
                        ? 'bg-blue-600 text-white scale-110'
                        : selectedScore > 0
                        ? 'bg-gray-100 text-gray-400'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {score}
                  </button>
                ))}
              </div>
            </div>

            {showNoteInput && (
              <div className="space-y-2">
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={selectedScore < 10 ? "Required: Why this score?" : "Optional: Add a note"}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  rows={2}
                  disabled={isSubmitting}
                />
                {selectedScore < 10 && !note.trim() && (
                  <p className="text-xs text-red-600">Note required for ratings below 10</p>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || selectedScore === 0 || (selectedScore < 10 && !note.trim())}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
                    ) : (
                      <><Send className="w-4 h-4" /> {isEditing ? 'Update' : 'Submit'}</>
                    )}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Other Users' Scores */}
      {otherScores.length > 0 && (
        <div className="border-t border-gray-200 pt-4 space-y-2">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">
            Other Ratings ({otherScores.length})
          </h4>
          {otherScores.map((score, idx) => (
            <div key={idx} className="flex items-start justify-between p-2 bg-gray-50 rounded">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{score.score}/10</span>
                  <span className="text-xs text-gray-500">
                    {score.userId.split('@')[0]}
                  </span>
                  <span className="text-xs text-gray-400">
                    {new Date(score.timestamp).toLocaleDateString()}
                  </span>
                </div>
                {score.note && (
                  <p className="text-sm text-gray-600 mt-1 italic">"{score.note}"</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
